import { useEffect, useState } from 'react';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import { CLASSIFICATION_EVENTS } from '../constants/events';

interface ClassificationDataState {
  posts: Post[];
  totalCount: number | null;
  isLoading: boolean;
  isSorting: boolean;
  hasTimedOut: boolean;
  hasError: boolean;
  error: string | null;
}

const MFE_HOST_TIMEOUT_MS = 10_000;

export const useClassificationPosts = () => {
  const [state, setState] = useState<ClassificationDataState>({
    posts: [],
    totalCount: null,
    isLoading: true,
    isSorting: false,
    hasTimedOut: false,
    hasError: false,
    error: null,
  });

  useEffect(() => {
  const handleUpdatePosts = (e: Event) => {
    const { posts: newPosts, totalCount, error: hasErrorResponse } = (e as CustomEvent<{ posts: Post[]; totalCount?: number; error?: boolean }>).detail ?? {};

    if (hasErrorResponse) {
      clearTimeout(hostTimeout);
      setState((prev) => ({ ...prev, posts: [], isLoading: false, isSorting: false, hasTimedOut: false, hasError: true }));
      return;
    }

    if (Array.isArray(newPosts)) {
      clearTimeout(hostTimeout);
      setState((prev) => ({
        posts: newPosts,
        totalCount: typeof totalCount === 'number' && Number.isFinite(totalCount) ? totalCount : prev.totalCount,
        isLoading: false,
        // Keep isSorting:true only if: we were sorting AND posts are empty AND totalCount isn't definitively 0
        // This handles hosts that send posts:[] as a clearing step before sending sorted results
        isSorting: prev.isSorting && newPosts.length === 0 && (typeof totalCount !== 'number' || totalCount > 0),
        hasTimedOut: false,
        hasError: false,
        error: null,
      }));
    }
  };

  const handleSortChange = (e: Event) => {
    const { key } = (e as CustomEvent<{ key: string }>).detail ?? {};
    setState((prev) => ({ ...prev, isSorting: true }));
    // Defer dispatch so React renders isSorting:true before the host responds.
    // Without this, React 18 batching can merge setState(isSorting:true) with
    // setState(isSorting:false) from handleUpdatePosts into a single render.
    setTimeout(() => {
      document.dispatchEvent(
        new CustomEvent(CLASSIFICATION_EVENTS.LOADED, {
          detail: { sortBy: key },
          bubbles: true,
          composed: true,
        }),
      );
    }, 0);
  };

  const handleInitPostCount = (e: Event) => {
    const { count } = (e as CustomEvent<{ count: number }>).detail ?? {};
    if (typeof count === 'number' && Number.isFinite(count)) {
      setState((prev) => ({ ...prev, totalCount: count }));
    }
  };

  const hostTimeout = setTimeout(() => {
    setState((prev) => {
      if (prev.isLoading) {
        return { ...prev, isLoading: false, hasTimedOut: true };
      }
      return prev;
    });
  }, MFE_HOST_TIMEOUT_MS);

  document.addEventListener(CLASSIFICATION_EVENTS.UPDATE_POSTS, handleUpdatePosts);
  globalThis.addEventListener('mfe-classify-sort-by-change', handleSortChange);
  document.addEventListener('mfe-classification-wall-init-post-count', handleInitPostCount);

  document.dispatchEvent(
    new CustomEvent(CLASSIFICATION_EVENTS.LOADED, {
      bubbles: true,
      composed: true,
    }),
  );

  return () => {
    document.removeEventListener(CLASSIFICATION_EVENTS.UPDATE_POSTS, handleUpdatePosts);
    globalThis.removeEventListener('mfe-classify-sort-by-change', handleSortChange);
    document.removeEventListener('mfe-classification-wall-init-post-count', handleInitPostCount);
    clearTimeout(hostTimeout);
  };
}, []);

  return state;
};

